/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccountVerification } from '../../../../providers/AccountVerification.provider';
import { useLayout } from '../../../../providers/Layout.provider';
import usePostAccountVerificationConfirm from '../../../../hooks/useApi/usePostAccountVerificationConfirm';
import VerificationForm from './VerificationForm';
import styles from '../AccountVerification.module.scss';

const { Box, Title, Term } = vp;

type ValuesType = {
  Amount1: string;
  Amount2: string;
};

type FC = FunctionComponent;

const AmountVerification: FC = () => {
  const history = useHistory();
  const { verificationInfo, verificationRequests, brandingInfo, verificationRequestsReset } = useAccountVerification();
  const [errorMessage, setErrorMessage] = useState('');
  const [errorMessageOriginal, setErrorMessageOriginal] = useState('');
  const [shouldConfirmVerification, setShouldConfirmVerification] = useState(false);
  const [accountVerificationConfirm, postAccountVerificationConfirm] = usePostAccountVerificationConfirm({});
  const { i18n, t } = useTranslation('translations', { keyPrefix: 'VerifyAmount' });
  const { setLayoutInfo } = useLayout();
  const handlers = {
    verifyAccount: (_values: ValuesType) => {
      if (Number(_values.Amount1) + Number(_values.Amount2) === 0) {
        setErrorMessage(t('enterAboveZero') || '');
      } else {
        postAccountVerificationConfirm({
          VerificationRequestID: verificationInfo.ExtraInfo.VerificationDetails?.VerificationRequestID || '',
          Token: verificationInfo.Token || '',
          Amount:
            _values.Amount1 === '' && _values.Amount2 !== ''
              ? `0.0${_values.Amount2}`
              : _values.Amount1 !== '' && _values.Amount2 === ''
              ? `0.${_values.Amount1}0`
              : `0.${_values.Amount1}${_values.Amount2}`,
        });
        setShouldConfirmVerification(true);
      }
    },
  };
  useEffect(() => {
    setLayoutInfo(
      vp.chooseColor<string | null | undefined>('#FFD147', brandingInfo?.BrandingDetails?.PrimaryColor),
      'curve',
      '',
      80,
      20,
      0,
      true,
      true,
    );
  }, [brandingInfo]);
  useEffect(() => {
    if (shouldConfirmVerification) {
      const { Success, ErrorMessage } = accountVerificationConfirm;

      verificationRequestsReset();

      if (Success && ErrorMessage === '') {
        history.push('/connect/verification/verified');
      } else if (ErrorMessage.includes('Invalid amount')) {
        setErrorMessageOriginal(ErrorMessage);
        setErrorMessage(
          `${t('verificationError')} ${t('tryAgain')} ${t('remainingAttempts', {
            x: 2 - 1 - Number(verificationRequests.VerificationRequests[0].VerificationAttempts),
          })}`,
        );
      } else if (ErrorMessage.includes('Invalid transaction')) {
        setErrorMessageOriginal(ErrorMessage);
        setErrorMessage(`${t('verificationAmountError')}`);
      } else {
        setErrorMessageOriginal(ErrorMessage);
        setErrorMessage(ErrorMessage);
      }
      setShouldConfirmVerification(false);
    }
  }, [accountVerificationConfirm, i18n.language]);
  useEffect(() => {
    if (errorMessageOriginal.includes('Invalid amount')) {
      setErrorMessage(
        `${t('verificationError')} ${t('tryAgain')} ${t('remainingAttempts', {
          x: 2 - 1 - Number(verificationRequests.VerificationRequests[0].VerificationAttempts),
        })}`,
      );
    } else if (errorMessageOriginal.includes('Invalid transaction')) {
      setErrorMessage(`${t('verificationAmountError')}`);
    } else {
      setErrorMessage(errorMessageOriginal);
    }
    setShouldConfirmVerification(false);
  }, [i18n.language]);

  useEffect(() => {
    if (verificationRequests.Success && verificationRequests.VerificationRequests[0].Status.Verification === 'failed') {
      history.push('/connect/verification/failed');
    }
  }, [verificationRequests]);

  return (
    <Box jc="center" ai="center" flx="col">
      <Box maxwidth="463px" flx="col" gap="common">
        {brandingInfo?.Logo && (
          <Box jc="center">
            <img
              width="35%"
              style={{
                minHeight: 'fit-content',
                maxWidth: '175px',
                maxHeight: '100px',
                width: 'auto',
                height: 'auto',
              }}
              alt="Logo"
              src={brandingInfo?.Logo}
            />
          </Box>
        )}
        <Title title={`${t('title')}`} size="lg" bold />
        <div className={styles.description}>{`${t('message')}`}</div>
        <VerificationForm handlers={handlers} errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
        <Term term={`${t('securityNote')}`} />
      </Box>
      <div className={styles.mobileImage} />
    </Box>
  );
};

export default AmountVerification;
